import { Injectable } from '@angular/core';

declare var cv: any;

export interface Corner {
  x: number;
  y: number;
}

export interface PerspectiveResult {
  image: HTMLImageElement;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root',
})
export class PerspectiveService {
  private cvLoaded = false;
  private cvLoading = false;
  private cvLoadPromise: Promise<boolean> | null = null;

  /**
   * Load OpenCV.js from CDN
   */
  async loadOpenCV(): Promise<boolean> {
    if (this.cvLoaded) return true;
    if (this.cvLoading && this.cvLoadPromise) return this.cvLoadPromise;

    this.cvLoading = true;
    this.cvLoadPromise = new Promise((resolve) => {
      // Check if already loaded
      if (typeof cv !== 'undefined' && cv.Mat) {
        this.cvLoaded = true;
        this.cvLoading = false;
        resolve(true);
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://docs.opencv.org/4.8.0/opencv.js';
      script.async = true;

      script.onload = () => {
        // OpenCV.js needs to be initialized
        const checkReady = () => {
          if (typeof cv !== 'undefined' && cv.Mat) {
            this.cvLoaded = true;
            this.cvLoading = false;
            console.log('OpenCV.js loaded successfully');
            resolve(true);
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      };

      script.onerror = () => {
        console.error('Failed to load OpenCV.js');
        this.cvLoading = false;
        resolve(false);
      };

      document.head.appendChild(script);
    });

    return this.cvLoadPromise;
  }

  /**
   * Check if OpenCV is available
   */
  isReady(): boolean {
    return this.cvLoaded && typeof cv !== 'undefined' && !!cv.Mat;
  }

  /**
   * Calculate distance between two points
   */
  calculateDistance(p1: Corner, p2: Corner): number {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  /**
   * Sort corners to ensure correct order: top-left, top-right, bottom-right, bottom-left
   */
  sortCorners(corners: Corner[]): Corner[] {
    if (corners.length !== 4) {
      throw new Error('Exactly 4 corners required');
    }

    // Find center point
    const centerX = corners.reduce((sum, c) => sum + c.x, 0) / 4;
    const centerY = corners.reduce((sum, c) => sum + c.y, 0) / 4;

    // Sort by angle from center
    const sorted = [...corners].sort((a, b) => {
      const angleA = Math.atan2(a.y - centerY, a.x - centerX);
      const angleB = Math.atan2(b.y - centerY, b.x - centerX);
      return angleA - angleB;
    });

    // Rotate to start with top-left (smallest x+y)
    const minIndex = sorted.reduce((minIdx, corner, idx, arr) => {
      const minSum = arr[minIdx].x + arr[minIdx].y;
      const currentSum = corner.x + corner.y;
      return currentSum < minSum ? idx : minIdx;
    }, 0);

    return [...sorted.slice(minIndex), ...sorted.slice(0, minIndex)];
  }

  /**
   * Apply perspective correction to an image using OpenCV
   */
  async applyPerspectiveCorrection(
    sourceImage: HTMLImageElement,
    corners: Corner[],
    scale: number = 1,
  ): Promise<PerspectiveResult> {
    // Ensure OpenCV is loaded
    if (!this.isReady()) {
      const loaded = await this.loadOpenCV();
      if (!loaded) {
        throw new Error('Failed to load OpenCV');
      }
    }

    if (corners.length !== 4) {
      throw new Error('Exactly 4 corners required for perspective correction');
    }

    // Scale corners if needed
    const scaledCorners = corners.map((c) => ({
      x: c.x * scale,
      y: c.y * scale,
    }));

    // Sort corners to correct order
    const sortedCorners = this.sortCorners(scaledCorners);

    // Calculate output dimensions
    const distances = [
      this.calculateDistance(sortedCorners[0], sortedCorners[1]), // top edge
      this.calculateDistance(sortedCorners[1], sortedCorners[2]), // right edge
      this.calculateDistance(sortedCorners[2], sortedCorners[3]), // bottom edge
      this.calculateDistance(sortedCorners[3], sortedCorners[0]), // left edge
    ];

    const outputWidth = Math.round(Math.max(distances[0], distances[2]));
    const outputHeight = Math.round(Math.max(distances[1], distances[3]));

    // Create source canvas
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = sourceImage.width;
    sourceCanvas.height = sourceImage.height;
    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) {
      throw new Error('Could not create canvas context');
    }
    sourceCtx.drawImage(sourceImage, 0, 0);

    // Create source and destination matrices
    const src = cv.imread(sourceCanvas);
    const dst = new cv.Mat();

    // Define source points (corners in the original image)
    const srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
      sortedCorners[0].x,
      sortedCorners[0].y,
      sortedCorners[1].x,
      sortedCorners[1].y,
      sortedCorners[2].x,
      sortedCorners[2].y,
      sortedCorners[3].x,
      sortedCorners[3].y,
    ]);

    // Define destination points (corners in the output image)
    const dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0,
      0,
      outputWidth,
      0,
      outputWidth,
      outputHeight,
      0,
      outputHeight,
    ]);

    // Calculate perspective transform matrix
    const M = cv.getPerspectiveTransform(srcPoints, dstPoints);

    // Apply perspective transform
    cv.warpPerspective(
      src,
      dst,
      M,
      new cv.Size(outputWidth, outputHeight),
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0, 0, 0, 0),
    );

    // Create output canvas
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;
    cv.imshow(outputCanvas, dst);

    // Clean up
    src.delete();
    dst.delete();
    srcPoints.delete();
    dstPoints.delete();
    M.delete();

    // Convert to image
    return new Promise((resolve, reject) => {
      const resultImage = new Image();
      resultImage.onload = () => {
        resolve({
          image: resultImage,
          width: outputWidth,
          height: outputHeight,
        });
      };
      resultImage.onerror = () =>
        reject(new Error('Failed to create result image'));
      resultImage.src = outputCanvas.toDataURL('image/png');
    });
  }

  /**
   * Draw corner markers on canvas (green circles with numbers)
   */
  drawCorners(
    ctx: CanvasRenderingContext2D,
    corners: Corner[],
    currentCornerIndex: number = -1,
    cornerRadius: number = 12,
  ): void {
    if (corners.length === 0) return;

    ctx.save();

    // Draw lines connecting corners
    if (corners.length >= 2) {
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      if (corners.length === 4) {
        ctx.closePath();
      }
      ctx.stroke();
    }

    // Draw corner circles
    corners.forEach((corner, index) => {
      // Outer glow
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, cornerRadius + 4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
      ctx.fill();

      // Main circle
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, cornerRadius, 0, Math.PI * 2);

      // Highlight current corner
      if (index === currentCornerIndex) {
        ctx.fillStyle = '#ffff00';
        ctx.strokeStyle = '#ff0000';
      } else {
        ctx.fillStyle = '#00ff00';
        ctx.strokeStyle = '#ffffff';
      }

      ctx.fill();
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw corner number
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), corner.x, corner.y);
    });

    ctx.restore();
  }

  /**
   * Draw transform handles on canvas (for crop/resize after perspective correction)
   * Draws 8 square handles at corners and mid-sides, plus a rotation handle
   */
  drawTransformHandles(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number = 0,
    handleSize: number = 20,
  ): void {
    ctx.save();

    // Move to center and apply rotation
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw dashed border
    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(-width / 2, -height / 2, width, height);
    ctx.setLineDash([]);

    // Handle positions (relative to center)
    const handles = [
      { x: -width / 2, y: -height / 2, cursor: 'nwse-resize' }, // top-left
      { x: 0, y: -height / 2, cursor: 'ns-resize' }, // top-center
      { x: width / 2, y: -height / 2, cursor: 'nesw-resize' }, // top-right
      { x: -width / 2, y: 0, cursor: 'ew-resize' }, // middle-left
      { x: width / 2, y: 0, cursor: 'ew-resize' }, // middle-right
      { x: -width / 2, y: height / 2, cursor: 'nesw-resize' }, // bottom-left
      { x: 0, y: height / 2, cursor: 'ns-resize' }, // bottom-center
      { x: width / 2, y: height / 2, cursor: 'nwse-resize' }, // bottom-right
    ];

    // Draw handles
    handles.forEach((handle) => {
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#1976d2';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, handleSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });

    // Draw rotation handle (above top center)
    const rotationHandleY = -height / 2 - 40;

    // Line connecting to rotation handle
    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, -height / 2);
    ctx.lineTo(0, rotationHandleY + 14);
    ctx.stroke();

    // Rotation handle circle
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, rotationHandleY, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Rotation icon
    ctx.fillStyle = '#1976d2';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('↻', 0, rotationHandleY);

    ctx.restore();
  }

  /**
   * Find which transform handle is at the given position
   * Returns: 'tl', 'tc', 'tr', 'ml', 'mr', 'bl', 'bc', 'br', 'rotate', 'move', or null
   */
  findTransformHandle(
    x: number,
    y: number,
    boxX: number,
    boxY: number,
    boxWidth: number,
    boxHeight: number,
    rotation: number = 0,
    threshold: number = 25,
  ): string | null {
    // Transform point to local coordinates (accounting for rotation)
    const centerX = boxX + boxWidth / 2;
    const centerY = boxY + boxHeight / 2;
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = x - centerX;
    const dy = y - centerY;
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    // Handle positions (relative to center)
    const handles: { id: string; x: number; y: number }[] = [
      { id: 'tl', x: -boxWidth / 2, y: -boxHeight / 2 },
      { id: 'tc', x: 0, y: -boxHeight / 2 },
      { id: 'tr', x: boxWidth / 2, y: -boxHeight / 2 },
      { id: 'ml', x: -boxWidth / 2, y: 0 },
      { id: 'mr', x: boxWidth / 2, y: 0 },
      { id: 'bl', x: -boxWidth / 2, y: boxHeight / 2 },
      { id: 'bc', x: 0, y: boxHeight / 2 },
      { id: 'br', x: boxWidth / 2, y: boxHeight / 2 },
      { id: 'rotate', x: 0, y: -boxHeight / 2 - 40 },
    ];

    // Check handles
    for (const handle of handles) {
      const dist = Math.sqrt(
        Math.pow(localX - handle.x, 2) + Math.pow(localY - handle.y, 2),
      );
      if (dist <= threshold) {
        return handle.id;
      }
    }

    // Check if inside box for move
    if (
      Math.abs(localX) <= boxWidth / 2 &&
      Math.abs(localY) <= boxHeight / 2
    ) {
      return 'move';
    }

    return null;
  }

  /**
   * Draw warp corner handles on canvas (diamond-shaped to distinguish from scale handles)
   */
  drawWarpCorners(
    ctx: CanvasRenderingContext2D,
    corners: Corner[],
    draggingIndex: number = -1,
    handleSize: number = 20,
  ): void {
    if (corners.length !== 4) return;

    ctx.save();

    // Draw quadrilateral outline
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 4; i++) {
      ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
    ctx.stroke();

    // Draw corner handles (diamond shape)
    corners.forEach((corner, index) => {
      ctx.save();
      ctx.translate(corner.x, corner.y);
      ctx.rotate(Math.PI / 4); // Rotate 45 degrees for diamond

      // Fill
      ctx.fillStyle = index === draggingIndex ? '#ffcc00' : '#ff6600';
      ctx.fillRect(-handleSize / 2, -handleSize / 2, handleSize, handleSize);

      // Border
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.strokeRect(-handleSize / 2, -handleSize / 2, handleSize, handleSize);

      ctx.restore();

      // Draw corner label
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), corner.x, corner.y);
    });

    ctx.restore();
  }

  /**
   * Initialize warp corners based on image bounds in canvas coordinates
   * Returns corners in order: top-left, top-right, bottom-right, bottom-left
   */
  initializeWarpCorners(
    centerX: number,
    centerY: number,
    width: number,
    height: number,
    rotation: number = 0,
  ): Corner[] {
    const halfW = width / 2;
    const halfH = height / 2;
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Local corners before rotation
    const localCorners = [
      { x: -halfW, y: -halfH }, // top-left
      { x: halfW, y: -halfH }, // top-right
      { x: halfW, y: halfH }, // bottom-right
      { x: -halfW, y: halfH }, // bottom-left
    ];

    // Apply rotation and translate to canvas coordinates
    return localCorners.map((c) => ({
      x: centerX + c.x * cos - c.y * sin,
      y: centerY + c.x * sin + c.y * cos,
    }));
  }

  /**
   * Apply perspective warp to transform an image to fit destination corners
   * Uses OpenCV for high-quality interpolation
   */
  async applyPerspectiveWarp(
    sourceImage: HTMLImageElement,
    destinationCorners: Corner[],
  ): Promise<{ image: HTMLImageElement; width: number; height: number; centerX: number; centerY: number }> {
    if (!this.isReady()) {
      throw new Error('OpenCV is not ready');
    }

    if (destinationCorners.length !== 4) {
      throw new Error('Exactly 4 destination corners required for warp');
    }

    // Calculate bounding box of destination corners
    const minX = Math.min(...destinationCorners.map((c) => c.x));
    const maxX = Math.max(...destinationCorners.map((c) => c.x));
    const minY = Math.min(...destinationCorners.map((c) => c.y));
    const maxY = Math.max(...destinationCorners.map((c) => c.y));
    const outputWidth = Math.round(maxX - minX);
    const outputHeight = Math.round(maxY - minY);

    if (outputWidth <= 0 || outputHeight <= 0) {
      throw new Error('Invalid warp dimensions');
    }

    // Normalize destination corners to output canvas space
    const normalizedDst = destinationCorners.map((c) => ({
      x: c.x - minX,
      y: c.y - minY,
    }));

    // Create source canvas
    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = sourceImage.width;
    srcCanvas.height = sourceImage.height;
    const srcCtx = srcCanvas.getContext('2d');
    if (!srcCtx) {
      throw new Error('Could not create canvas context');
    }
    srcCtx.drawImage(sourceImage, 0, 0);

    // OpenCV matrices
    const srcMat = cv.imread(srcCanvas);

    // Source corners (full image rectangle)
    const srcCornersMat = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0,
      0,
      sourceImage.width,
      0,
      sourceImage.width,
      sourceImage.height,
      0,
      sourceImage.height,
    ]);

    // Destination corners (warped quadrilateral)
    const dstCornersMat = cv.matFromArray(4, 1, cv.CV_32FC2, [
      normalizedDst[0].x,
      normalizedDst[0].y,
      normalizedDst[1].x,
      normalizedDst[1].y,
      normalizedDst[2].x,
      normalizedDst[2].y,
      normalizedDst[3].x,
      normalizedDst[3].y,
    ]);

    // Calculate and apply perspective transform
    const transformMatrix = cv.getPerspectiveTransform(
      srcCornersMat,
      dstCornersMat,
    );
    const dstMat = new cv.Mat();

    cv.warpPerspective(
      srcMat,
      dstMat,
      transformMatrix,
      new cv.Size(outputWidth, outputHeight),
      cv.INTER_CUBIC,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0, 0, 0, 0), // Transparent background
    );

    // Create output canvas
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;
    cv.imshow(outputCanvas, dstMat);

    // Clean up OpenCV matrices
    srcMat.delete();
    srcCornersMat.delete();
    dstCornersMat.delete();
    transformMatrix.delete();
    dstMat.delete();

    // Convert to image
    return new Promise((resolve, reject) => {
      const resultImage = new Image();
      resultImage.onload = () => {
        resolve({
          image: resultImage,
          width: outputWidth,
          height: outputHeight,
          centerX: (minX + maxX) / 2,
          centerY: (minY + maxY) / 2,
        });
      };
      resultImage.onerror = () =>
        reject(new Error('Failed to create warped image'));
      resultImage.src = outputCanvas.toDataURL('image/png');
    });
  }

  /**
   * Find corner at given coordinates
   */
  findCornerAtPosition(
    corners: Corner[],
    x: number,
    y: number,
    threshold: number = 20,
  ): number {
    for (let i = 0; i < corners.length; i++) {
      const distance = this.calculateDistance(corners[i], { x, y });
      if (distance <= threshold) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Check if a point is inside a quadrilateral defined by 4 corners
   */
  isPointInsideQuad(corners: Corner[], x: number, y: number): boolean {
    if (corners.length !== 4) return false;

    let inside = false;
    const n = corners.length;

    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = corners[i].x,
        yi = corners[i].y;
      const xj = corners[j].x,
        yj = corners[j].y;

      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }

    return inside;
  }
}
